/**
 * JahresStatistikBarChart Widget - Eigenständige Version für das neue Widget-System
 */
import React, { useState, useEffect } from 'react';
import WidgetFilterBox from './WidgetFilterBox';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import moment from "moment";
import { fetchApi } from "../../../utils/DatabaseHelper";
import { WIDGET_JAHRESSTATISTIK } from "../../../utils/TableConstanten";
import logger from "../../../logging/logger";
import './JahresStatistikBarChart.scss';

const JahresStatistikBarChart = ({ config, data, onDataChange, onConfigChange }) => {
  // The default structure is now mainly for initialization, the backend will provide the full year.
  const jahresStatistikDefault = [
    { name: 'JAN', red: 0, yellow: 0, green: 0 },
    { name: 'FEB', red: 0, yellow: 0, green: 0 },
    { name: 'MAR', red: 0, yellow: 0, green: 0 },
    { name: 'APR', red: 0, yellow: 0, green: 0 },
    { name: 'MAI', red: 0, yellow: 0, green: 0 },
    { name: 'JUN', red: 0, yellow: 0, green: 0 },
    { name: 'JUL', red: 0, yellow: 0, green: 0 },
    { name: 'AUG', red: 0, yellow: 0, green: 0 },
    { name: 'SEP', red: 0, yellow: 0, green: 0 },
    { name: 'OKT', red: 0, yellow: 0, green: 0 },
    { name: 'NOV', red: 0, yellow: 0, green: 0 },
    { name: 'DEZ', red: 0, yellow: 0, green: 0 },
  ];

  const [statusFilter] = useState([
    {name: "Alle", id: "all"},
    {name: "Gültig", id: "green"},
    {name: "Bald fällig", id: "yellow"},
    {name: "Fällig", id: "red"},
  ]);
  
  const [yearsFilter, setYearsFilter] = useState([]); // Will be populated from backend
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [chartData, setChartData] = useState(jahresStatistikDefault);
  const [query, setQuery] = useState({
    y: moment().year(),
    s: "all"
  });

  // Farben aus der Konfiguration oder Standardwerte
  const color = {
    red: '#dc2626',
    yellow: '#fbbf24',
    green: '#a3e635'
  };

  const validate = () => {
    let params = "";
    if (typeof query === 'object') {
        const queryString = Object.entries(query)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
        params = `?${queryString}`;
    }

    fetchApi(WIDGET_JAHRESSTATISTIK, "", params).then(resultSet => {
        if (resultSet && resultSet.result && resultSet.filterYears) {
            const newData = resultSet.result;
            const newYearFilter = resultSet.filterYears.map(d => ({ name: d.jahr, id: d.jahr }));

            // Only update state if data has actually changed to prevent infinite loops
            if(JSON.stringify(chartData) !== JSON.stringify(newData)){
                setChartData(newData);
            }
            if(JSON.stringify(yearsFilter) !== JSON.stringify(newYearFilter)){
                setYearsFilter(newYearFilter);
            }
        }
    }).catch(err => {
        logger.error("Fehler beim Abrufen der Jahresstatistik:", err);
        // Optionally reset to default or show an error state
        setChartData(jahresStatistikDefault);
    });
  }

  useEffect(() => {
    validate();
    
    // Event-Handler für das REFRESH_DASHBOARD-Event
    const handleRefreshDashboard = () => {
        validate();
    };
    
    // Füge Event-Listener hinzu
    global.emitter.on('REFRESH_DASHBOARD', handleRefreshDashboard);
    
    // Entferne Event-Listener beim Unmounting
    return () => {
        global.emitter.off('REFRESH_DASHBOARD', handleRefreshDashboard);
    };
  }, [query]);

  const getCell = (index) => {
    const currentMonth = moment().month(); // 0-indexed
    const currentYear = moment().year();
    const selectedYearValue = selectedYear;

    let opacity = 0.6; // Default for past months

    if (selectedYearValue > currentYear) {
        opacity = 0.3; // Future year
    } else if (selectedYearValue == currentYear) {
        if (index > currentMonth) {
            opacity = 0.3; // Future month in current year
        } else if (index === currentMonth) {
            opacity = 1.0; // Current month in current year
        }
    }
    // Past months in current year or any month in a past year keeps opacity 0.6

    return <Cell key={`cell-${index}`} opacity={opacity} />;
  }

  const onStatusChange = (e) => {
    const newStatus = e.getAttribute("data-query");
    setQuery(prevQuery => ({
        ...prevQuery,
        s: newStatus
    }));
  }

  const onYearsChange = (e) => {
    const newYear = e.getAttribute("data-query");
    setSelectedYear(newYear);
    setQuery(prevQuery => ({
        ...prevQuery,
        y: newYear
    }));
  }

  const handleBarClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
        const payload = data.activePayload[0].payload;
        const month = payload.monat;
        const selectedYearValue = selectedYear;

        // Prevent clicking on past months/blocks
        const currentMonth = moment().month() + 1; // 1-indexed
        const currentYear = moment().year();
        
        // Check if clicked month is in the past
        if (selectedYearValue < currentYear || (selectedYearValue === currentYear && month < currentMonth)) {
            return; // Do nothing for past months
        }

        if (month && selectedYearValue) {
            const viewDate = moment({ year: selectedYearValue, month: month - 1, day: 1 }).format('YYYY-MM-DD');

            // Determine the status filter to apply
            const currentStatusFilter = query.s;
            let mappedStatus = null;
            if (currentStatusFilter === 'red' || currentStatusFilter === 'yellow' || currentStatusFilter === 'green') {
                mappedStatus = currentStatusFilter;
            }

            if (mappedStatus) {
                localStorage.setItem('detailSort', mappedStatus);
            } else {
                localStorage.removeItem('detailSort');
            }

            window.location.hash = `#vorsorgenkartei?viewDate=${viewDate}`;
        }
    }
  }

  return (
    <div className="jahres-statistik-bar-chart-widget">
      <div className="col">
        <div className="justify-content-center widget-overview">
          <WidgetFilterBox
              statusFilter={statusFilter}
              onStatusChange={onStatusChange}
              yearsFilter={yearsFilter}
              onYearsChange={onYearsChange}
              selectedStatus={query.s}
              selectedYear={selectedYear}
          />
          <ResponsiveContainer width="100%" height={225}>
            <BarChart
              tabIndex={1}
              width={500}
              height={300}
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
              onClick={handleBarClick}
            >
              <CartesianGrid
                stroke="#F8F7F7"
                strokeDasharray="0"
                vertical={false}
              />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="red" name="Fällig" stackId="a" fill={color.red}
                   onMouseEnter={() => { document.body.style.cursor = 'pointer'; }}
                   onMouseLeave={() => { document.body.style.cursor = 'default'; }}>
                {chartData.map((entry, index) =>
                  getCell(index)
                )}
              </Bar>
              <Bar
                dataKey="yellow"
                name="Bald fällig"
                stackId="a"
                fill={color.yellow}
                onMouseEnter={() => { document.body.style.cursor = 'pointer'; }}
                onMouseLeave={() => { document.body.style.cursor = 'default'; }}>
                {chartData.map((entry, index) =>
                  getCell(index)
                )}
              </Bar>
              <Bar dataKey="green" name="Gültig" stackId="a" fill={color.green}
                   onMouseEnter={() => { document.body.style.cursor = 'pointer'; }}
                   onMouseLeave={() => { document.body.style.cursor = 'default'; }}>
                {chartData.map((entry, index) =>
                  getCell(index)
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default JahresStatistikBarChart;
